'use client';

import { Popover, RadioGroup } from 'radix-ui';
import { useState } from 'react';
import { useSafePrefetch } from '../../../lib/hooks/use-safe-prefetch';
import { cn } from '../../../lib/utils';
import {
  type SortOption,
  useProductListingSortState,
} from './use-product-listing-sort-state';

export function ProductListingSortSelect({
  prefetchSort,
  defaultSort,
  className,
}: {
  prefetchSort: (sort: SortOption) => void;
  defaultSort: SortOption;
  className?: string;
}) {
  const { sort, updateSort, sortOptions } =
    useProductListingSortState(defaultSort);
  const { safePrefetch } = useSafePrefetch(prefetchSort);
  const [open, setOpen] = useState(false);

  const handleSortChange = (value: string) => {
    updateSort(value as SortOption);
    setOpen(false);
  };

  return (
    <section className="relative">
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button type="button" className={cn('flex items-center', className)}>
            <span className="flex items-center justify-center gap-3 whitespace-nowrap text-sm">
              Sort by
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={cn(
                  'h-[0.85em] w-[0.85em] transition-transform duration-200',
                  {
                    'rotate-180': open,
                  }
                )}
                fill="none"
                viewBox="0 0 24 24"
              >
                <title>Sort</title>
                <path
                  fill="currentColor"
                  fillRule="evenodd"
                  d="M5.293 8.293a1 1 0 0 1 1.414 0L12 13.586l5.293-5.293a1 1 0 1 1 1.414 1.414l-6 6a1 1 0 0 1-1.414 0l-6-6a1 1 0 0 1 0-1.414Z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            className={cn(
              'z-50 min-w-[240px] overflow-hidden bg-white p-4 shadow-xl drop-shadow-md data-[state=closed]:animate-dropdown-close data-[state=open]:animate-dropdown-open',
              className
            )}
            align="end"
            sideOffset={0}
          >
            <div className="w-full">
              <RadioGroup.Root
                value={sort || 'BEST_SELLING'}
                onValueChange={handleSortChange}
                className="grid gap-3"
              >
                {sortOptions.map((option) => (
                  <label
                    key={option.value}
                    className={`sort--${option.value} flex cursor-pointer items-center space-x-2 font-normal leading-[1.4]`}
                    htmlFor={option.value}
                    onMouseEnter={() => {
                      if (option.value !== sort) {
                        safePrefetch(option.value);
                      }
                    }}
                    onTouchStart={() => {
                      if (option.value !== sort) {
                        safePrefetch(option.value);
                      }
                    }}
                  >
                    <span className="relative flex h-4 w-4 items-center justify-center">
                      <RadioGroup.Item
                        id={option.value}
                        value={option.value}
                        className="peer h-full w-full appearance-none rounded-full border border-[hsl(var(--color))] bg-white"
                      >
                        <RadioGroup.Indicator className="absolute top-0.5 left-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-[hsl(var(--color))]" />
                      </RadioGroup.Item>
                    </span>
                    <span className="max-w-[220px] truncate">
                      {option.label}
                    </span>
                  </label>
                ))}
              </RadioGroup.Root>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </section>
  );
}
